import { Formik } from "formik";
import React, { useState } from "react";
import { Alert, Button, Container, Form, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import * as yup from 'yup';
import { useHistory } from 'react-router';

import "../../styles/Register.scss";
import Navigation from "../Navigation";
import Auth from "./Auth";

const schema = yup.object().shape({
  name: yup.string().required('Name is required')
    .max(255, 'Name must not exceed 255 characters'),
  email: yup.string()
    .required('Email is required')
    .email('Email is invalid'),
  password: yup.string()
    .required('Password is required')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})/,
      "Minimum 8 characters. Number, upper and lower case characters required"
    ),
  organization: yup.string()
    .required('Organization name is required')
    .max(255, 'Name must not exceed 255 characters'),
  terms: yup.boolean().oneOf([true], `Must Accept Terms and Conditions`),
});

const Register = (props: any) => {
  const history = useHistory();

  const [registrationStatus, setRegistrationStatus] = useState<'form' | 'success' | 'exists'>('form');
  const [registrationEmail, setRegistrationEmail] = useState('');
  const [errorState, setErrorState] = useState(false);

  const submit = async (data: any) => {
    const {name, email, password, organization, terms} = data;

    const auth = new Auth();
    
    try {
      setErrorState(false);
      const result = await auth.registerUser(name, email, password, organization, terms);
      setRegistrationEmail(email);
      setRegistrationStatus('success');
    } catch (error) {
      console.log(error);
      switch(error.message) {
        case '409':
          setRegistrationStatus('exists');
          break;
        default:
          setErrorState(true);
      }
    }
  }

  const renderForm = () => {
    return (
      <div className="mt-3 w-50">
        <h2>Create new account</h2>
        {
            errorState && 
            <Alert variant="danger">
              An error occurred during registration.
            </Alert>
          }
        <div className="bg-dark p-4 rounded-sm my-3">
        <Formik
          initialValues={{
            name: "",
            email: "",
            password: "",
            organization: "",
            terms: false
          }}
          validationSchema={schema}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={async (values, { setSubmitting, resetForm }) => {
            setSubmitting(true);
            await submit(values);
          }}
        >
          {({
            values,
            errors,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
          }) => (
            <Form onSubmit={handleSubmit} autoComplete="off">
              <Form.Group controlId="name">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  autoFocus
                  autoComplete="off"
                  type="text"
                  name="name"
                  placeholder="Your name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={!!errors.name}
                />
                <Form.Text className="">
                    Add your full name                
                </Form.Text>
                <Form.Control.Feedback type="invalid">
                  {errors.name}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group controlId="email">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  autoComplete="off"
                  type="email"
                  name="email"
                  placeholder="Your email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={!!errors.email}
                />
                <Form.Text className="">
                  Enter a unique email address             
                </Form.Text>
                <Form.Control.Feedback type="invalid">
                  {errors.email}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group controlId="password">
                <Form.Label>Password</Form.Label>
                <Form.Control
                  autoComplete="off"
                  type="password"
                  name="password"
                  placeholder="Password"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={!!errors.password}
                />
                <Form.Text className="">
                  Minimum 8 characters. Number, upper and lower case characters required               
                </Form.Text>
                <Form.Control.Feedback type="invalid">
                  {errors.password}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group controlId="organization">
                <Form.Label>Organization</Form.Label>
                <Form.Control
                  autoComplete="off"
                  type="text"
                  name="organization"
                  placeholder="Organization name"
                  value={values.organization}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={!!errors.organization}
                />
                <Form.Text className="">
                  Enter a business or personal name
                </Form.Text>
                <Form.Control.Feedback type="invalid">
                  {errors.organization}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group controlId="terms">
                <Form.Check 
                  type="checkbox" 
                  name="terms" 
                  onChange={handleChange}
                  onBlur={handleBlur}
                  checked={values.terms}
                  isInvalid={!!errors.terms}
                  label="You agree to our Terms and Data Policy" />
              </Form.Group>
              <Button
                variant="primary"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting && 
                  <Spinner
                    className="mr-2"
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                />
                }
                Join
              </Button>
            </Form>
          )}
        </Formik>
        </div>
        If you are trying to join an existing account click here to <Link to="/login">log in</Link>.
      </div>
    );
  }

  const renderAccountCreated = () => {
    return (
      <div>
        <h2>Confirmation</h2>
        <div className="mt-3" style={{fontSize: '1.2em'}}>A confirmation e-mail has been sent to {registrationEmail}.</div>
        <div className="mt-3">
          <Button 
            variant="primary"
            size="sm"
            onClick={() => {
              history.push('/login');
            }}
          >
            Continue to Login
          </Button>
        </div>
      </div>
    );
  }

  const renderAccountAlreadyExists = () => {
    return (
      <div>
        <h2>Error</h2>
        <div className="mt-3" style={{fontSize: '1.2em'}}>A user with this e-mail already exists.</div>
        <div className="mt-3 d-flex justify-content-between">
        <Button 
            variant="primary"
            size="sm"
            onClick={() => setRegistrationStatus('form')}
          >
            Back to registration
          </Button>
          <Button 
            variant="primary"
            size="sm"
            onClick={() => {
              history.push('/login');
            }}
          >
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  const render = () => {
    switch(registrationStatus) {
      case 'form':
        return renderForm()
      case 'success':
        return renderAccountCreated()
      case 'exists':
        return renderAccountAlreadyExists()
    }
  }

  return (
    <div>
      <Navigation showDashboardOptions={false}/>
      <Container className="dashboard_container d-flex flex-column align-items-center mt-5">
          {render()}
      </Container>
    </div>
  )
}

export default Register;